import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  MapPinIcon,
  ClockIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { RootState, AppDispatch } from '../store/store';
import { trackOrder, cancelOrder } from '../store/slices/orderSlice';

const OrderTrackingPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const { trackingInfo, isLoading, error } = useSelector(
    (state: RootState) => state.order
  );

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    if (orderId) {
      dispatch(trackOrder(orderId));
      setLastUpdated(new Date());
    }
  }, [orderId, dispatch]);

  useEffect(() => {
    if (!autoRefresh || !orderId) return;

    const interval = setInterval(() => {
      dispatch(trackOrder(orderId));
      setLastUpdated(new Date());
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, orderId, dispatch]);

  const handleRefresh = () => {
    if (orderId) {
      dispatch(trackOrder(orderId));
      setLastUpdated(new Date());
    }
  };

  const handleCancelOrder = async () => {
    if (!orderId || !trackingInfo) return;

    const confirmed = window.confirm(
      'Are you sure you want to cancel this order? This action cannot be undone.'
    );

    if (confirmed) {
      await dispatch(cancelOrder(orderId));
    }
  };

  const getStatusIcon = (status: string, isActive: boolean) => {
    if (status === 'cancelled') {
      return <XCircleIcon className="w-6 h-6 text-red-500" />;
    }

    return (
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center ${
          isActive
            ? 'bg-green-500 text-white'
            : 'bg-gray-200 text-gray-400'
        }`}
      >
        {isActive ? <CheckCircleIcon className="w-4 h-4" /> : ''}
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'text-blue-600 bg-blue-100';
      case 'preparing':
        return 'text-yellow-600 bg-yellow-100';
      case 'ready':
        return 'text-purple-600 bg-purple-100';
      case 'out_for_delivery':
        return 'text-orange-600 bg-orange-100';
      case 'delivered':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const canCancelOrder = trackingInfo?.status === 'confirmed' || trackingInfo?.status === 'preparing';

  const estimatedDelivery = trackingInfo?.estimatedTime
    ? new Date(trackingInfo.estimatedTime)
    : new Date(Date.now() + 45 * 60 * 1000);

  if (isLoading && !trackingInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order tracking...</p>
        </div>
      </div>
    );
  }

  if (error && !trackingInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Unable to Track Order
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-x-4">
            <button
              onClick={handleRefresh}
              className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Try Again
            </button>
            <Link
              to="/"
              className="bg-gray-100 hover:bg-gray-200 text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            📍 Track Your Order
          </h1>
          <p className="text-gray-600">
            Order #{trackingInfo?.orderId || orderId}
          </p>
          <div className="mt-4 flex items-center justify-center space-x-4">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoRefresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="autoRefresh" className="text-sm text-gray-600">
                Auto-refresh
              </label>
            </div>
            <span className="text-xs text-gray-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Tracking Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Current Status */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Order Status
                </h2>
                {trackingInfo && (
                  <span
                    className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(
                      trackingInfo.status
                    )}`}
                  >
                    {trackingInfo.status.replace('_', ' ').toUpperCase()}
                  </span>
                )}
              </div>

              {/* Progress Timeline */}
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                <div className="space-y-8">
                  {[
                    {
                      key: 'confirmed',
                      title: 'Order Confirmed',
                      description: 'We received your order and payment',
                    },
                    {
                      key: 'preparing',
                      title: 'Preparing Your Food',
                      description: 'Our chefs are cooking your meal',
                    },
                    {
                      key: 'ready',
                      title: 'Ready for Pickup',
                      description: 'Your order is ready for delivery',
                    },
                    {
                      key: 'out_for_delivery',
                      title: 'Out for Delivery',
                      description: 'Your order is on the way',
                    },
                    {
                      key: 'delivered',
                      title: 'Delivered',
                      description: 'Enjoy your meal!',
                    },
                  ].map((step) => {
                    const isActive = trackingInfo?.status === step.key;
                    const isCompleted = trackingInfo?.updates?.some(
                      update => update.status === step.key
                    );
                    const isVisible = isActive || isCompleted;

                    return (
                      <div key={step.key} className="relative flex items-start">
                        <div className="relative z-10 bg-white">
                          {getStatusIcon(step.key, isVisible || false)}
                        </div>
                        <div className="ml-4">
                          <h3
                            className={`font-medium ${
                              isVisible ? 'text-gray-900' : 'text-gray-500'
                            }`}
                          >
                            {step.title}
                          </h3>
                          <p
                            className={`text-sm ${
                              isVisible ? 'text-gray-600' : 'text-gray-400'
                            }`}
                          >
                            {step.description}
                          </p>
                          {trackingInfo?.updates && (
                            <>
                              {trackingInfo.updates
                                .filter(update => update.status === step.key)
                                .map((update, index) => (
                                  <p key={index} className="text-xs text-green-600 mt-1">
                                    {new Date(update.timestamp).toLocaleTimeString()} - {update.message}
                                  </p>
                                ))}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Order Items - Only available from Order data, not tracking */}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Delivery Information */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="w-5 h-5 mr-2" />
                Delivery Details
              </h3>

              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-medium text-gray-900">
                    Customer Name
                  </p>
                  <p className="text-gray-600">
                    Delivery Address
                  </p>
                </div>

                <div className="flex items-center text-gray-600">
                  <PhoneIcon className="w-4 h-4 mr-2" />
                  <span>Phone Number</span>
                </div>

                <div className="flex items-center text-gray-600">
                  <ClockIcon className="w-4 h-4 mr-2" />
                  <span>
                    Est. {estimatedDelivery.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Summary - Only available from Order data, not tracking */}

            {/* Actions */}
            <div className="space-y-3">
              {canCancelOrder && (
                <button
                  onClick={handleCancelOrder}
                  className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                >
                  Cancel Order
                </button>
              )}

              <Link
                to="/menu"
                className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 px-4 rounded-lg font-medium text-center block transition-colors"
              >
                Order Again
              </Link>

              <Link
                to="/order-history"
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-3 px-4 rounded-lg font-medium text-center block transition-colors"
              >
                View Order History
              </Link>
            </div>

            {/* Support */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Need Help?</h4>
              <p className="text-sm text-blue-800 mb-3">
                Having issues with your order?
              </p>
              <div className="flex items-center text-sm text-blue-800">
                <PhoneIcon className="w-4 h-4 mr-2" />
                <span>(555) 123-VOICE</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingPage;